import { useCallback, useRef, useEffect } from 'react';
import L from 'leaflet';
import { useLeaflet } from 'react-leaflet';
import { makeStyles } from '@material-ui/core/styles';

import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

import { AreaModes, AreaTypes, IArea } from '../models/types';
import { getColor } from '../theme';
import {
  extractLayerCoords,
  extractMarkerCoords,
  isValidLayer,
  compareCoordinates,
  isRectangle,
  isCircle,
  isPolygon,
  isMarker,
  hasGeoman,
  cleanupTemporaryLayers,
  DEFAULT_SHAPE_COLOR,
  DEFAULT_FILL_OPACITY
} from '../utils/mapEditControl.utils';
import {
  IGeomanCreateEvent,
  IGeomanEditEvent,
  IGeomanUpdateEvent,
  IGeomanMarkerDragEndEvent,
  IListenerRef,
  IGeomanControlConfig
} from '../types/geoman';
import {
  EDIT_OPTIONS,
  TIMING,
  hasListener,
  getActiveDrawShape,
  createMarkerIcon,
  createMarkerIconForDraw,
  setupMarkerDraggingWithCleanup,
  getGeomanShapeName,
  getCommonDrawOptions
} from '../utils/geomanUtils';


/**
 * Reference to temporary layers created during drawing
 */
const temporaryLayersRef = new WeakMap<L.Map, Set<L.Layer>>();

/**
 * Shared hook for Geoman map control logic
 * Extracts common functionality between Main and Infrastructure EditControl components
 */
export const useGeomanControl = (config: IGeomanControlConfig) => {
  const {
    mode,
    type,
    setAreaCoordinates,
    setBeaconCoordinates,
    setOfficeCoordinates,
    setCameraCoordinates,
    setMarkerEditMode,
    setBoxEditMode,
    setCircleEditMode,
    setPolygonEditMode,
    areaEdit,
    MarkerIcon
  } = config;

  const classes = useStyles();
  const { map } = useLeaflet();
  const listenerRefs = useRef<IListenerRef[]>([]);
  const prevModeRef = useRef<AreaModes | null>(null);
  const prevTypeRef = useRef<AreaTypes | null>(null);
  const timeoutRefs = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const markerCleanupRefs = useRef<Map<L.Marker, () => void>>(new Map());

  /**
   * Update marker coordinates - handles Main (all types) and Infrastructure (beacon only)
   */
  const updateMarkerCoordinates = useCallback(
    (coords: number[]) => {
      if (setOfficeCoordinates && setCameraCoordinates) {
        // Main version: update all marker types
        setOfficeCoordinates(coords);
        setBeaconCoordinates(coords);
        setCameraCoordinates(coords);
      } else {
        // Infrastructure version: only beacon
        setBeaconCoordinates(coords);
      }
    },
    [setBeaconCoordinates, setOfficeCoordinates, setCameraCoordinates]
  );

  /**
   * Setup marker dragging with proper initialization and cleanup
   * Returns cleanup function to properly manage timeouts
   */
  const setupMarkerDragging = useCallback(
    (marker: L.Marker): (() => void) => {
      const cleanupFunctions: Array<() => void> = [];

      // Set draggable option
      marker.options = marker.options || {};
      marker.options.draggable = true;

      // Initialize dragging handler - must be done synchronously to prevent drag issues
      const initDragging = () => {
        try {
          // Initialize dragging handler if it doesn't exist
          if (!marker.dragging && marker._initDragging) {
            marker._initDragging();
          }

          // Enable dragging if handler exists
          if (marker.dragging && !marker.dragging.enabled()) {
            marker.dragging.enable();
          }
        } catch (e) {
          console.warn('Failed to initialize marker dragging:', e);
        }
      };

      // Initialize dragging immediately when marker is already on map
      // or setup listener for when it's added
      if (marker._map) {
        // Marker is already on map - initialize dragging immediately (synchronously)
        // to prevent race conditions where drag starts before dragging handler is ready
        initDragging();
      } else {
        // Wait for marker to be added to map, then initialize
        const onMarkerAdd = () => {
          initDragging();
          marker.off('add', onMarkerAdd);
        };
        marker.on('add', onMarkerAdd);
        cleanupFunctions.push(() => marker.off('add', onMarkerAdd));
      }

      // Add dragend listener for coordinate updates
      // Cast through unknown to satisfy TypeScript's strict type checking
      const markerLayer = (marker as unknown) as L.Layer;
      if (!hasListener(listenerRefs.current, markerLayer, 'dragend')) {
        const dragEndHandler = () => {
          const coords = extractMarkerCoords(marker);
          if (coords?.length) {
            updateMarkerCoordinates(coords);
          }
        };
        marker.on('dragend', dragEndHandler);
        listenerRefs.current.push({
          layer: markerLayer,
          event: 'dragend',
          handler: dragEndHandler
        });
      }

      // Return cleanup function
      return () => {
        cleanupFunctions.forEach(cleanup => cleanup());
      };
    },
    [updateMarkerCoordinates]
  );

  /**
   * Attach polygon-specific event listeners
   * CRITICAL: Remove old listeners before adding new ones to prevent closure issues
   */
  const attachPolygonListeners = useCallback(
    (
      layer: L.Layer,
      updateHandler: (e: IGeomanEditEvent | IGeomanUpdateEvent | IGeomanMarkerDragEndEvent) => void
    ) => {
      const normalizeEvent = (e: any) => ({
        layer: e.layer || layer,
        target: e.target || layer,
        // Use Geoman's getShape() method (official API) - https://geoman.io/docs/leaflet/modes/edit-mode
        shape: e.shape || (hasGeoman(layer) && layer.pm ? layer.pm.getShape() : undefined),
        ...(e.indexPath !== undefined && {
          indexPath: e.indexPath,
          markerEvent: e.markerEvent,
          intersectionReset: e.intersectionReset
        })
      });

      const events = [
        'pm:markerdragend',
        'pm:vertexadded',
        'pm:vertexremoved',
        'pm:rotate',
        'pm:rotateend'
      ];

      // Remove old listeners for this layer FIRST to prevent closure issues
      const oldListenersForLayer = listenerRefs.current.filter(
        ref => ref.layer === layer && events.includes(ref.event)
      );
      oldListenersForLayer.forEach(ref => {
        try {
          layer.off(ref.event, ref.handler);
        } catch (e) {
          console.warn('[Geoman] Error removing old listener:', e);
        }
      });
      listenerRefs.current = listenerRefs.current.filter(
        ref => !(ref.layer === layer && events.includes(ref.event))
      );

      const handlers: Array<{ event: string; handler: (e: any) => void }> = [];
      
      handlers.push({ event: 'pm:markerdragend', handler: (e: any) => updateHandler(normalizeEvent(e)) });
      handlers.push({ event: 'pm:vertexadded', handler: (e: any) => updateHandler(normalizeEvent(e)) });
      handlers.push({ event: 'pm:vertexremoved', handler: (e: any) => updateHandler(normalizeEvent(e)) });
      handlers.push({ event: 'pm:rotate', handler: (e: any) => updateHandler(normalizeEvent(e)) });
      handlers.push({ event: 'pm:rotateend', handler: (e: any) => updateHandler(normalizeEvent(e)) });

      handlers.forEach(({ event, handler }) => {
        (layer as any).on(event, handler);
        listenerRefs.current.push({ layer, event, handler: handler as any });
      });
    },
    []
  );

  /**
   * Enable editing on a layer with basic retry for React-Leaflet delays
   * Returns cleanup function for timeout management
   */
  const enableEditingWithRetry = useCallback(
    (layer: L.Layer, updateHandler: (e: any) => void, retries = 2): (() => void) | void => {
      if (!map?.hasLayer(layer)) {
        return;
      }

      if (hasGeoman(layer) && layer.pm) {
        try {
          // Ensure edit mode is enabled
          if (!layer.pm.enabled()) {
            layer.pm.enable(EDIT_OPTIONS);
          }

          // Attach appropriate event listeners (duplicate prevention built-in)
          if (isPolygon(layer)) {
            attachPolygonListeners(layer, updateHandler);
          } else {
            // For Circle layers Geoman may not emit pm:edit on radius/drag changes,
            // so we subscribe to dedicated events
            if (isCircle(layer)) {
              // Обновляем координаты круга только по завершающим событиям, чтобы не спамить во время перетаскивания
              const circleEndEvents = [
                'pm:dragend',
                'pm:markerdragend',
                'pm:radiuschanged',
                'pm:centerchanged',
                'moveend',
                'pm:update'
              ];
              circleEndEvents.forEach(eventName => {
                if (!hasListener(listenerRefs.current, layer, eventName)) {
                  (layer as any).on(eventName, updateHandler);
                  listenerRefs.current.push({ layer, event: eventName, handler: updateHandler });
                }
              });

              // Polling fallback for radius changes (handles builds that don't emit radius events reliably)
              const anyLayer = layer as any;
              if (!anyLayer._radiusPollHooksAttached) {
                const startRadiusPolling = () => {
                  try {
                    if (anyLayer._radiusPoll) return;
                    anyLayer._lastRadius = (layer as L.Circle).getRadius();
                    anyLayer._radiusPoll = setInterval(() => {
                      try {
                        const newRadius = (layer as L.Circle).getRadius();
                        if (typeof newRadius === 'number' && newRadius !== anyLayer._lastRadius) {
                          anyLayer._lastRadius = newRadius;
                          // отмечаем, что радиус менялся; обновим координаты один раз при завершении
                          anyLayer._radiusChanged = true;
                        }
                      } catch {
                        // ignore polling errors
                      }
                    }, 120);
                  } catch {
                    // ignore polling start errors
                  }
                };

                const stopRadiusPolling = () => {
                  try {
                    if (anyLayer._radiusPoll) {
                      clearInterval(anyLayer._radiusPoll);
                      anyLayer._radiusPoll = null;
                    }
                    // если в процессе были изменения радиуса — единожды обновим координаты по завершению
                    if (anyLayer._radiusChanged) {
                      anyLayer._radiusChanged = false;
                      updateHandler({ layer, target: layer, shape: 'Circle' });
                    }
                  } catch {
                    // ignore polling stop errors
                  }
                };

                // Attach start/stop handlers once per layer
                (layer as any).on('pm:dragstart', startRadiusPolling);
                listenerRefs.current.push({ layer, event: 'pm:dragstart', handler: startRadiusPolling });

                (layer as any).on('pm:markerdragstart', startRadiusPolling);
                listenerRefs.current.push({ layer, event: 'pm:markerdragstart', handler: startRadiusPolling });

                (layer as any).on('pm:radiuschange', startRadiusPolling);
                listenerRefs.current.push({ layer, event: 'pm:radiuschange', handler: startRadiusPolling });

                (layer as any).on('pm:dragend', stopRadiusPolling);
                listenerRefs.current.push({ layer, event: 'pm:dragend', handler: stopRadiusPolling });

                (layer as any).on('pm:markerdragend', stopRadiusPolling);
                listenerRefs.current.push({ layer, event: 'pm:markerdragend', handler: stopRadiusPolling });

                (layer as any).on('pm:radiuschanged', stopRadiusPolling);
                listenerRefs.current.push({ layer, event: 'pm:radiuschanged', handler: stopRadiusPolling });

                (layer as any).on('moveend', stopRadiusPolling);
                listenerRefs.current.push({ layer, event: 'moveend', handler: stopRadiusPolling });

                anyLayer._radiusPollHooksAttached = true;
              }
            }

            // Attach ending events for Rectangle/Square to capture size/move edits
            if (isRectangle(layer)) {
              const rectEvents = [
                'pm:markerdragend',
                'pm:vertexadded',
                'pm:vertexremoved',
                'pm:dragend',
                'pm:resize',
                'pm:change'
              ];
              
              const oldRectListeners = listenerRefs.current.filter(
                ref => ref.layer === layer && rectEvents.includes(ref.event)
              );
              oldRectListeners.forEach(ref => {
                try {
                  layer.off(ref.event, ref.handler);
                } catch (e) {
                  console.warn('[Geoman] Error removing old rect listener:', e);
                }
              });
              listenerRefs.current = listenerRefs.current.filter(
                ref => !(ref.layer === layer && rectEvents.includes(ref.event))
              );
              
              rectEvents.forEach(eventName => {
                (layer as any).on(eventName, updateHandler);
                listenerRefs.current.push({ layer, event: eventName, handler: updateHandler });
              });
            }

            // Fallback update after edit mode ends
            if (!hasListener(listenerRefs.current, layer, 'pm:update')) {
              layer.on('pm:update', updateHandler);
              listenerRefs.current.push({ layer, event: 'pm:update', handler: updateHandler });
            }
          }
        } catch (e) {
          if (retries > 0) {
            const timeoutId = setTimeout(
              () => enableEditingWithRetry(layer, updateHandler, retries - 1),
              TIMING.EDIT_RETRY_DELAY
            );
            timeoutRefs.current.add(timeoutId);
            return () => {
              clearTimeout(timeoutId);
              timeoutRefs.current.delete(timeoutId);
            };
          } else {
            console.warn('Failed to enable editing or attach listeners on layer after retries:', e);
          }
        }
      }
    },
    [attachPolygonListeners, map]
  );

  /**
   * Create coordinate update handler for a layer
   * Directly updates coordinates without debouncing to ensure all changes are captured
   */
  const createUpdateHandler = useCallback(
    (layer: L.Layer) => (e: any) => {
      const coords = extractLayerCoords(layer);
      if (coords?.length) {
        const shape = e.shape || layer.pm?.getShape();
        const isValid =
          (shape === 'Polygon' && coords.length >= 6) ||
          (['Rectangle', 'Square'].includes(shape) && coords.length === 4) ||
          (shape === 'Circle' && coords.length === 3) ||
          !shape;
        
        if (isValid) {
          setAreaCoordinates(coords);
        }
      }
    },
    [setAreaCoordinates]
  );

  /**
   * Check if a layer matches the area being edited (Main only)
   */
  const layerMatchesArea = useCallback(
    (layer: L.Layer, area: IArea | null | undefined): boolean => {
      if (!area) {
        return false;
      }

      try {
        // Prefer explicit layer areaId match if provided by layer options (fast path)
        const layerAreaId = (layer as any)?.options?.areaId;
        if (layerAreaId != null && String(layerAreaId) === String(area.id)) {
          return true;
        }

        // Try to match both global and local coordinates to support geo/non-geo modes
        const candidates = [area.geomGlobalCoords, area.geomCoords].filter(
          (c: any) => Array.isArray(c) && c.length > 0
        ) as number[][];

        if (candidates.length === 0) {
          return false;
        }

        const layerCoords = extractLayerCoords(layer);
        if (!layerCoords || layerCoords.length === 0) {
          return false;
        }

        // Check if layer type matches area type
        const typeMatches =
          (area.geomType === AreaTypes.Box && isRectangle(layer)) ||
          (area.geomType === AreaTypes.Circle && isCircle(layer)) ||
          (area.geomType === AreaTypes.Polygon && isPolygon(layer));

        if (typeMatches) {
          return candidates.some(candidate => compareCoordinates(layerCoords, candidate));
        }
      } catch {
        return false;
      }

      return false;
    },
    []
  );

  /**
   * Handle shape creation events
   */
  const handleCreate = useCallback(
    (e: IGeomanCreateEvent) => {
      const { layer, shape } = e;
      const coords = extractLayerCoords(layer);

      if (!coords || coords.length === 0) {
        return;
      }

      // Update coordinates based on shape type
      switch (shape) {
        case 'Rectangle':
        case 'Square': {
          if (mode === AreaModes.Edit && setBoxEditMode) {
            setBoxEditMode();
          }
          setAreaCoordinates(coords);
          break;
        }
        case 'Circle': {
          if (mode === AreaModes.Edit && setCircleEditMode) {
            setCircleEditMode();
          }
          setAreaCoordinates(coords);
          break;
        }
        case 'Polygon': {
          if (mode === AreaModes.Edit && setPolygonEditMode) {
            setPolygonEditMode();
          }
          setAreaCoordinates(coords);
          break;
        }
        case 'Marker': {
          if (mode === AreaModes.Edit && setMarkerEditMode) {
            setMarkerEditMode();
          }

          // Setup marker dragging
          if (isMarker(layer)) {
            const marker = layer as L.Marker;
            const icon = createMarkerIcon(MarkerIcon, classes.icon, classes.draggableMarker);
            marker.setIcon(icon);

            setupMarkerDraggingWithCleanup(marker, markerCleanupRefs.current, setupMarkerDragging);
          }

          updateMarkerCoordinates(coords);
          break;
        }
      }

      // Mark layer as temporary (created during drawing, not saved yet)
      layer._isTemporaryDrawingLayer = true;

      // Store reference to temporary layer for explicit cleanup
      if (map && !temporaryLayersRef.has(map)) {
        temporaryLayersRef.set(map, new Set());
      }
      if (map) {
        temporaryLayersRef.get(map)!.add(layer);
      }

      // Keep layer visible in Create mode so user can see what they drew
      // Disable draw mode after creating a shape, but enable editing so user can continue modifying it
      switch (mode) {
        case AreaModes.Create:
          // Disable draw mode after creating one shape
          if (map) {
            try {
              map.pm.disableDraw();
            } catch (e) {
              console.warn('Failed to disable draw mode:', e);
            }
          }

          // Enable editing on shapes (Polygon, Rectangle, Circle)
          // Layer is already created at this point (pm:create event fired), so we can enable editing immediately
          if (!isMarker(layer) && hasGeoman(layer) && isValidLayer(layer)) {
            try {
              // Verify layer still exists on map and map is still available
              if (map && map.hasLayer(layer)) {
                // For shapes: enable Geoman edit mode
                const updateHandler = createUpdateHandler(layer);
                enableEditingWithRetry(layer, updateHandler);
              }
            } catch (e) {
              console.warn('Failed to enable editing on newly created layer:', e);
            }
          }
          break;
        case AreaModes.Edit:
          // Keep layer visible in edit mode
          break;
        default:
          // Remove layer in other modes (shouldn't happen, but just in case)
          if (map) {
            try {
              map.removeLayer(layer);
            } catch (e) {
              console.warn('Failed to remove layer:', e);
            }
          }
          break;
      }
    },
    [
      mode,
      map,
      setAreaCoordinates,
      setBeaconCoordinates,
      setBoxEditMode,
      setCircleEditMode,
      setPolygonEditMode,
      setMarkerEditMode,
      updateMarkerCoordinates,
      MarkerIcon,
      createUpdateHandler,
      enableEditingWithRetry,
      setupMarkerDragging
    ]
  );

  /**
   * Handle shape edit events
   */
  const handleEdit = useCallback(
    (e: IGeomanEditEvent) => {
      const layer = e.target || e.layer;

      // Don't update coordinates for markers - they use Leaflet's native dragend instead
      if (isMarker(layer)) {
        return;
      }

      const coords = extractLayerCoords(layer);
      if (!coords || coords.length === 0) {
        return;
      }

      const shape = e.shape || (hasGeoman(layer) && layer.pm ? layer.pm.getShape() : undefined);

      // Set corresponding edit mode based on shape type (Main only)
      if (setBoxEditMode && setCircleEditMode && setPolygonEditMode) {
        switch (shape) {
          case 'Rectangle':
          case 'Square':
            setBoxEditMode();
            break;
          case 'Circle':
            setCircleEditMode();
            break;
          case 'Polygon':
            setPolygonEditMode();
            break;
          default:
            return;
        }
      }

      setAreaCoordinates(coords);
    },
    [setAreaCoordinates, setBoxEditMode, setCircleEditMode, setPolygonEditMode]
  );

  /**
   * Handle Geoman error events
   */
  const handleError = useCallback((e: any) => {
    console.warn('Geoman error:', e.source, e.message, e.payload);
  }, []);

  /**
   * Initialize Geoman configuration and set up event handlers
   */
  useEffect(() => {
    if (!map) {
      return;
    }

    try {
      // Set Russian language for Geoman tooltips
      map.pm.setLang('ru');

      // Set default path options for drawn shapes
      map.pm.setPathOptions({
        color: DEFAULT_SHAPE_COLOR,
        fillColor: DEFAULT_SHAPE_COLOR,
        fillOpacity: DEFAULT_FILL_OPACITY
      });
    } catch (e) {
      console.warn('Failed to initialize Geoman:', e);
    }

    // Set up map-level event listeners
    // Geoman events are not in Leaflet's type definitions, use type assertion
    (map as any).on('pm:create', handleCreate);
    (map as any).on('pm:edit', handleEdit);
    (map as any).on('pm:update', handleEdit); // Use handleEdit for both edit and update events
    (map as any).on('pm:error', handleError);

    return () => {
      try {
        // Clean up map-level event listeners
        (map as any).off('pm:create', handleCreate);
        (map as any).off('pm:edit', handleEdit);
        (map as any).off('pm:update', handleEdit);
        (map as any).off('pm:error', handleError);

        // Clean up all stored layer-level listeners
        listenerRefs.current.forEach(({ layer, event, handler }) => {
          if (layer && typeof layer.off === 'function') {
            try {
              layer.off(event, handler);
            } catch (e) {
              console.warn('Error during listener cleanup:', e);
            }
          }
        });
        listenerRefs.current = [];

        // Clean up marker dragging cleanup functions
        markerCleanupRefs.current.forEach(cleanup => cleanup());
        markerCleanupRefs.current.clear();
      } catch (e) {
        console.warn('Error during cleanup:', e);
      }
    };
  }, [map, handleCreate, handleEdit, handleError]);

  /**
   * Handle mode changes and enable/disable editing accordingly
   */
  useEffect(() => {
    if (!map) {
      return;
    }

    const modeChanged = prevModeRef.current !== mode;
    const typeChanged = prevTypeRef.current !== type;

    prevModeRef.current = mode;
    prevTypeRef.current = type;

    // No pending updates to flush since we update immediately now

    // Clean up temporary layers when switching to Default mode
    if (mode === AreaModes.Default) {
      cleanupTemporaryLayers(map, temporaryLayersRef);
    }

    // Disable draw/edit modes when switching away from Create mode or to different type
    // Don't disable if we're staying in Create mode with the same type (prevents interrupting drawing)
    if (modeChanged && mode !== AreaModes.Create) {
      // Switching away from Create mode - disable everything
      try {
        map.pm.disableDraw();
        map.pm.disableGlobalEditMode();
      } catch (e) {
        console.warn('Failed to disable draw/edit modes:', e);
      }
    } else if (typeChanged && mode === AreaModes.Create) {
      // Switching shape type in Create mode - disable current draw mode and global edit mode
      try {
        map.pm.disableDraw();
        map.pm.disableGlobalEditMode();
      } catch (e) {
        console.warn('Failed to disable draw/edit modes:', e);
      }

      // Clean up any temporary layers from previous shape to allow switching draw mode cleanly
      cleanupTemporaryLayers(map, temporaryLayersRef);
    }

    // Always disable global edit mode if not in Edit mode (we use per-layer editing instead)
    if (mode !== AreaModes.Edit) {
      try {
        map.pm.disableGlobalEditMode();
      } catch (e) {
        console.warn('Failed to disable global edit mode:', e);
      }
    }

    if (mode === AreaModes.Create && temporaryLayersRef.has(map)) {
      temporaryLayersRef.get(map)!.forEach(layer => {
        if (map.hasLayer(layer)) {
          const updateHandler = createUpdateHandler(layer);
          if (isPolygon(layer)) {
            attachPolygonListeners(layer, updateHandler);
          } else if (isCircle(layer) || isRectangle(layer)) {
            enableEditingWithRetry(layer, updateHandler);
          }
        }
      });
    }

    // Combined iteration: cleanup, validation, and enable editing in single pass
    const isMarkerType = type === AreaTypes.Marker;
    const invalidLayers: L.Layer[] = [];

    map.eachLayer((layer: any) => {
      try {
        // Collect invalid layers for later processing (only in Create mode)
        if (mode === AreaModes.Create && hasGeoman(layer) && !isValidLayer(layer)) {
          invalidLayers.push(layer);
        }

        // Enable editing based on mode
        if (mode === AreaModes.Edit) {
          const isLayerMarker = isMarker(layer);
          if (isMarkerType === isLayerMarker) {
            if (isLayerMarker) {
              const cleanup = setupMarkerDragging(layer as L.Marker);
              if (cleanup) {
                markerCleanupRefs.current.set(layer as L.Marker, cleanup);
              }
            } else if (areaEdit ? layerMatchesArea(layer, areaEdit) : isValidLayer(layer)) {
              console.log('[Geoman] Re-enabling editing on layer in Edit mode');
              const updateHandler = createUpdateHandler(layer);
              enableEditingWithRetry(layer, updateHandler);
            }
          }
        } else if (mode === AreaModes.Create && isMarker(layer) && type === AreaTypes.Marker) {
          // In Create mode with Marker type, ensure all markers are draggable
          setupMarkerDraggingWithCleanup(
            layer as L.Marker,
            markerCleanupRefs.current,
            setupMarkerDragging
          );
        }
      } catch (e) {
        console.warn('Error processing layer:', e);
      }
    });

    // Handle mode-specific logic
    switch (mode) {
      case AreaModes.Edit: {
        // Snapping is disabled by default in Geoman, and we disable it per-layer when enabling edit
        break;
      }
      case AreaModes.Create: {
        // Disable pm on invalid layers
        invalidLayers.forEach(layer => {
          if (hasGeoman(layer)) {
            try {
              layer.pm.disable();
            } catch (e) {
              console.warn('Failed to disable invalid layer:', e);
            }
          }
        });

        // Setup global modes based on type
        // Disable global modes to prevent conflicts - rely on per-layer editing instead
        map.pm.disableGlobalEditMode();
        map.pm.disableGlobalDragMode();

        // Process temporary layers
        let hasTemporaryLayer = false;
        const temporaryLayers: L.Layer[] = [];
        if (temporaryLayersRef.has(map)) {
          temporaryLayersRef.get(map)!.forEach(layer => {
            if (map.hasLayer(layer)) {
              hasTemporaryLayer = true;
              temporaryLayers.push(layer);
              if (isMarker(layer)) {
                setupMarkerDraggingWithCleanup(
                  layer as L.Marker,
                  markerCleanupRefs.current,
                  setupMarkerDragging
                );
              } else if (hasGeoman(layer) && isValidLayer(layer) && !layer.pm?.enabled()) {
                layer.pm.enable();
              }
            }
          });
        }

        // Check current draw mode state using Geoman API
        const currentDrawShape = getActiveDrawShape(map);
        const expectedDrawShape = getGeomanShapeName(type);

        // Enable draw mode if no temporary layer exists (shape not yet created in this session)
        // Draw mode should be enabled regardless of existing map data
        if (!hasTemporaryLayer && expectedDrawShape) {
          // Only enable if draw mode is not already active for this shape type
          // This prevents interrupting active drawing sessions
          if (currentDrawShape !== expectedDrawShape) {
            try {
              switch (type) {
                case AreaTypes.Box:
                  map.pm.enableDraw('Rectangle', getCommonDrawOptions());
                  break;
                case AreaTypes.Circle:
                  map.pm.enableDraw('Circle', getCommonDrawOptions());
                  break;
                case AreaTypes.Polygon:
                  // Enable polygon drawing - vertex markers appear automatically
                  map.pm.enableDraw(
                    'Polygon',
                    getCommonDrawOptions({
                      templineStyle: { color: DEFAULT_SHAPE_COLOR }, // Line between vertices
                      hintlineStyle: { color: DEFAULT_SHAPE_COLOR, dashArray: [5, 5] } // Line to cursor
                    })
                  );
                  break;
                case AreaTypes.Marker: {
                  const customIcon = createMarkerIconForDraw(
                    MarkerIcon,
                    classes.icon,
                    classes.draggableMarker
                  );

                  map.pm.enableDraw(
                    'Marker',
                    getCommonDrawOptions({
                      markerStyle: {
                        icon: customIcon,
                        draggable: true // Ensure marker is created as draggable
                      }
                    })
                  );
                  break;
                }
              }
            } catch (e) {
              console.warn('Failed to enable draw mode:', e);
            }
          }
        } else if (hasTemporaryLayer && currentDrawShape) {
          // Temporary layer exists - shape was created, disable draw mode
          try {
            map.pm.disableDraw();
          } catch (e) {
            console.warn('Failed to disable draw mode:', e);
          }
        }
        break;
      }
      case AreaModes.Default: {
        // Temporary layers cleaned up at the beginning of useEffect
        break;
      }
    }
  }, [
    mode,
    type,
    map,
    areaEdit,
    MarkerIcon,
    createUpdateHandler,
    enableEditingWithRetry,
    setupMarkerDragging,
    layerMatchesArea
  ]);

  /**
   * Cleanup on component unmount
   */
  useEffect(() => {
    if (!map) {
      return;
    }

    return () => {
      // Clean up listener references
      listenerRefs.current.forEach(({ layer, event, handler }) => {
        layer?.off?.(event, handler);
      });
      listenerRefs.current = [];

      // Clean up all pending timeouts
      timeoutRefs.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      timeoutRefs.current.clear();

      // Clean up marker dragging cleanup functions
      markerCleanupRefs.current.forEach(cleanup => cleanup());
      markerCleanupRefs.current.clear();

      // Clean up temporary layers
      cleanupTemporaryLayers(map, temporaryLayersRef);
    };
  }, [map]);
};

/**
 * Styles for marker icon
 */
const useStyles = makeStyles(() => ({
  icon: {
    color: getColor('constant-blueberry-light'),
    fontSize: 32,
    width: '1em',
    height: '1em',
    pointerEvents: 'auto' // Ensure marker icon can receive mouse events
  },
  draggableMarker: {
    pointerEvents: 'auto'
  }
}));
